import React, { useEffect, useState } from "react";
import axios from "axios";
import { Instagram, MessageCircle, Clock, ShieldCheck, Phone, CalendarDays, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, PageHero } from "@/components/ui-kit";
import { LeadMagnet } from "@/components/LeadMagnet";
import { FAQSection } from "@/components/Sections";
import { usePageMeta } from "@/hooks/usePageMeta";
import { CONFIG } from "@/config";

const CHANNELS = [
  {
    icon: CalendarDays,
    title: "Book a consultation",
    line: "Pick a slot — phone, WhatsApp, Google Meet or in person at our office.",
    cta: "Choose a time",
    href: "/book",
    testId: "contact-book",
    internal: true,
  },
  {
    icon: Phone,
    title: CONFIG.phone,
    line: "Call us directly to speak with the team about your goals.",
    cta: "Call now",
    href: CONFIG.phoneHref,
    testId: "contact-phone",
    sameTab: true,
  },
  {
    icon: MessageCircle,
    title: "WhatsApp an expert",
    line: "Fastest way to reach us. Ask a quick question or book a conversation.",
    cta: "Open WhatsApp",
    href: CONFIG.whatsappUrl,
    testId: "contact-whatsapp",
  },
  {
    icon: Instagram,
    title: `Instagram ${CONFIG.instagramHandle}`,
    line: "Follow for simple explanations and send us a DM anytime.",
    cta: "Visit Instagram",
    href: CONFIG.instagramUrl,
    testId: "contact-instagram",
  },
];

const HoursCard = () => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/booking/config`).then(({ data }) => setCfg(data)).catch(() => setCfg(false));
  }, []);
  return (
    <div data-testid="contact-hours" className="flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-lightbg/60 p-7">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/[0.05] text-royal"><Clock className="h-6 w-6" /></span>
      <h3 className="mt-6 font-display text-xl font-bold text-navy">Consultation hours</h3>
      {cfg === null && <p className="mt-2 text-sm text-ink/50">Loading…</p>}
      {cfg === false && <p className="mt-2 text-[15px] text-ink/65">By appointment — book a slot or call us.</p>}
      {cfg && (
        <>
          <p data-testid="contact-hours-days" className="mt-2 font-display text-lg font-bold text-navy">{cfg.days_label}</p>
          <p data-testid="contact-hours-time" className="text-[15px] text-ink/65">{cfg.hours} IST</p>
          {cfg.closed_days.length > 0 && <p className="mt-1 text-xs text-ink/50">Closed: {cfg.closed_days.join(", ")}</p>}
        </>
      )}
      <Link to="/book" className="mt-auto pt-5 text-sm font-semibold text-royal">Book within these hours →</Link>
    </div>
  );
};

const ChannelLink = ({ c, className, children }) =>
  c.internal ? (
    <Link to={c.href} data-testid={c.testId} className={className}>{children}</Link>
  ) : (
    <a href={c.href} target={c.sameTab ? undefined : "_blank"} rel={c.sameTab ? undefined : "noopener noreferrer"} data-testid={c.testId} className={className}>{children}</a>
  );

export default function ContactPage() {
  usePageMeta({
    title: "Contact Auxieron — Talk to a Financial Expert",
    description: "Share your goals and get a personalised financial plan from Auxieron, Bhubaneswar. Reach us on WhatsApp or Instagram, or send an enquiry through our form.",
    path: "/contact",
  });

  return (
    <>
      <PageHero
        label="Contact"
        title="Let's talk about"
        highlight="your next step."
        lead="Whether you're starting your first SIP, comparing loan options or planning retirement, share a few details and our team will reach out with a clear, no-pressure starting point."
        testId="contact-label"
      />

      <section className="bg-white py-14 sm:py-16">
        <div className="mx-auto grid max-w-7xl gap-5 px-5 sm:px-8 md:grid-cols-2 lg:grid-cols-3">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <ChannelLink
                c={c}
                className="group flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-lightbg/60 p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-hover"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/[0.05] text-royal transition-colors group-hover:bg-royal group-hover:text-white">
                  <c.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold text-navy">{c.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink/65">{c.line}</p>
                <span className="mt-auto pt-5 text-sm font-semibold text-royal">{c.cta} →</span>
              </ChannelLink>
            </Reveal>
          ))}
          <Reveal delay={0.1}><HoursCard /></Reveal>
          <Reveal delay={0.12}>
            <div data-testid="contact-address" className="flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-lightbg/60 p-7">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/[0.05] text-royal"><MapPin className="h-6 w-6" /></span>
              <h3 className="mt-6 font-display text-xl font-bold text-navy">Visit our office</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-ink/65">{CONFIG.address}</p>
              <p className="mt-auto pt-5 text-xs text-ink/50">In-person meetings by appointment — book a slot above.</p>
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <div data-testid="contact-expectations" className="flex h-full flex-col rounded-2xl border border-navy/[0.08] bg-navy p-7 text-white">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-gold">
                <Clock className="h-6 w-6" />
              </span>
              <h3 className="mt-6 font-display text-xl font-bold">What happens next</h3>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed text-white/75">
                <li>1. You share your goals through the form below.</li>
                <li>2. We review your situation — usually within 1–2 working days.</li>
                <li>3. We reach out to suggest a sensible starting point.</li>
              </ul>
              <p className="mt-auto flex items-center gap-2 pt-5 text-xs text-white/60">
                <ShieldCheck className="h-4 w-4 text-gold" /> Your details stay private and are used only for your enquiry.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <LeadMagnet />
      <FAQSection />
    </>
  );
}
